import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import {
  Card,
  Button,
  Input,
  Textarea,
  PageHeader,
  Spinner,
  EmptyState,
  Modal,
  Field,
  Tabs,
  ErrorBanner,
  formatDateTime,
} from '../components/ui';

interface Community {
  id: string;
  name: string;
  description?: string;
}

interface Channel {
  id: string;
  name: string;
  description?: string;
}

interface Post {
  id: string;
  body: string;
  authorId: string;
  reactions?: { emoji: string; count: number }[];
  commentCount?: number;
  createdAt: string;
}

interface DirectMessage {
  id: string;
  body: string;
  fromUserId: string;
  toUserId: string;
  createdAt: string;
}

type Tab = 'channels' | 'dms';

export function CommunityPage() {
  const [tab, setTab] = useState<Tab>('channels');
  const [activeCommunity, setActiveCommunity] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['communities'],
    queryFn: () => api.get<{ data: Community[] }>('/communities').catch(() => ({ data: [] })),
  });

  const communities = q.data?.data ?? [];

  return (
    <div>
      <PageHeader title="Community" subtitle="Channels, posts, and DMs for your members" />
      <Tabs<Tab>
        active={tab}
        onChange={setTab}
        tabs={[
          { value: 'channels', label: 'Channels' },
          { value: 'dms', label: 'Direct messages' },
        ]}
      />
      {tab === 'channels' && (
        <>
          {q.isLoading ? (
            <Spinner />
          ) : communities.length === 0 ? (
            <Card>
              <EmptyState title="No communities yet" description="Create one to start discussions." />
            </Card>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 'var(--space-3)' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                {communities.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setActiveCommunity(c.id)}
                    style={{
                      padding: 'var(--space-2) var(--space-3)',
                      borderRadius: 'var(--radius-md)',
                      border: 'none',
                      background: c.id === activeCommunity ? 'var(--color-brand-primary-light)' : 'transparent',
                      color: c.id === activeCommunity ? 'var(--color-brand-primary)' : 'inherit',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: 'var(--text-sm)',
                    }}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
              {activeCommunity ? <ChannelView communityId={activeCommunity} /> : <EmptyState title="Pick a community" />}
            </div>
          )}
        </>
      )}
      {tab === 'dms' && <DmView />}
    </div>
  );
}

function ChannelView({ communityId }: { communityId: string }) {
  const [activeChannel, setActiveChannel] = useState<string | null>(null);
  const channelsQ = useQuery({
    queryKey: ['channels', communityId],
    queryFn: () => api.get<{ data: Channel[] }>(`/communities/${communityId}/channels`).catch(() => ({ data: [] })),
  });
  const channels = channelsQ.data?.data ?? [];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 'var(--space-3)' }}>
      <div>
        {channels.map((ch) => (
          <button
            key={ch.id}
            onClick={() => setActiveChannel(ch.id)}
            style={{
              display: 'block',
              width: '100%',
              padding: 'var(--space-1) var(--space-2)',
              border: 'none',
              background: ch.id === activeChannel ? 'var(--color-surface-tertiary)' : 'transparent',
              cursor: 'pointer',
              textAlign: 'left',
              fontSize: 'var(--text-sm)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            # {ch.name}
          </button>
        ))}
        {channels.length === 0 && (
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)' }}>No channels yet.</p>
        )}
      </div>
      {activeChannel ? <PostsView communityId={communityId} channelId={activeChannel} /> : (
        <Card>
          <EmptyState title="Pick a channel" />
        </Card>
      )}
    </div>
  );
}

function PostsView({ communityId, channelId }: { communityId: string; channelId: string }) {
  const qc = useQueryClient();
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['posts', communityId, channelId],
    queryFn: () => api.get<{ data: Post[] }>(`/communities/${communityId}/channels/${channelId}/posts`).catch(() => ({ data: [] })),
  });

  const postMut = useMutation({
    mutationFn: () => api.post(`/communities/${communityId}/channels/${channelId}/posts`, { body }),
    onSuccess: () => {
      setBody('');
      qc.invalidateQueries({ queryKey: ['posts', communityId, channelId] });
    },
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Failed'),
  });

  const reactMut = useMutation({
    mutationFn: ({ postId, emoji }: { postId: string; emoji: string }) =>
      api.post(`/communities/${communityId}/posts/${postId}/react`, { emoji }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['posts', communityId, channelId] }),
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <Card style={{ padding: 'var(--space-3)' }}>
        <Textarea value={body} onChange={setBody} rows={2} placeholder="Share something with the community…" />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--space-2)' }}>
          <Button onClick={() => body && postMut.mutate()} disabled={!body || postMut.isPending}>
            Post
          </Button>
        </div>
        <ErrorBanner error={error} />
      </Card>

      {q.isLoading ? (
        <Spinner />
      ) : (q.data?.data ?? []).length === 0 ? (
        <EmptyState title="No posts yet" description="Be the first to post." />
      ) : (
        (q.data?.data ?? []).map((p) => (
          <Card key={p.id}>
            <div style={{ fontSize: 'var(--text-sm)' }}>{p.body}</div>
            <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', marginTop: 'var(--space-2)', fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
              <span>{formatDateTime(p.createdAt)}</span>
              <button
                onClick={() => reactMut.mutate({ postId: p.id, emoji: '👍' })}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 'inherit', color: 'inherit' }}
              >
                👍 {(p.reactions?.find((r) => r.emoji === '👍')?.count ?? 0)}
              </button>
              <span>💬 {p.commentCount ?? 0}</span>
            </div>
          </Card>
        ))
      )}
    </div>
  );
}

function DmView() {
  const qc = useQueryClient();
  const [recipient, setRecipient] = useState('');
  const [body, setBody] = useState('');

  const q = useQuery({
    queryKey: ['dms'],
    queryFn: () => api.get<{ data: DirectMessage[] }>('/messages/dm').catch(() => ({ data: [] })),
  });

  const sendMut = useMutation({
    mutationFn: () => api.post('/messages/dm', { toUserId: recipient, body }),
    onSuccess: () => {
      setBody('');
      qc.invalidateQueries({ queryKey: ['dms'] });
    },
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <Card>
        <h4 style={{ margin: '0 0 var(--space-2)', fontSize: 'var(--text-sm)', fontWeight: 600 }}>New message</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 'var(--space-2)' }}>
          <Input value={recipient} onChange={setRecipient} placeholder="Recipient user ID" />
          <Input value={body} onChange={setBody} placeholder="Message…" />
          <Button onClick={() => recipient && body && sendMut.mutate()} disabled={!recipient || !body || sendMut.isPending}>
            Send
          </Button>
        </div>
      </Card>
      {q.isLoading ? (
        <Spinner />
      ) : (q.data?.data ?? []).length === 0 ? (
        <Card>
          <EmptyState title="No DMs yet" />
        </Card>
      ) : (
        (q.data?.data ?? []).map((d) => (
          <Card key={d.id}>
            <div style={{ fontSize: 'var(--text-sm)' }}>{d.body}</div>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 4 }}>
              {formatDateTime(d.createdAt)}
            </div>
          </Card>
        ))
      )}
    </div>
  );
}
